import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Table, Tag, Button, Space, Spin, Alert, Typography, Badge, message } from 'antd';
import { ReloadOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getPanelVinculacionIncoherencias,
  type PanelIncoherencias,
  type Inc01Row,
  type Inc02Row,
  type Inc04Row,
  type Inc05Row,
  type Inc06Row,
  type Inc07Row,
  type Inc08Row,
  type Inc09Row,
  type BiunivocidadSubtipo,
} from '../../api/vinculacionApi';
import { fct } from '../../fct/tokens/tokens';
import { FctEstado, FctGlifoAtencion, claseFilaAtencion, type FctNivelAtencion } from '../../fct/react';
import { FilterBtn } from './helpers';

const { Title, Text, Paragraph } = Typography;

// ── Helpers ─────────────────────────────────────────────────────────
const pctStr = (x: number) => `${Math.round(x * 100)}%`;

const num = (a?: number, b?: number) => (a ?? 0) - (b ?? 0);
const txt = (a?: string, b?: string) => (a ?? '').localeCompare(b ?? '');

/** Descarga un CSV (separador ';', BOM para Excel). */
const exportCsv = (filename: string, headers: string[], rows: (string | number)[][]) => {
  const esc = (v: string | number) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.map(esc).join(';'), ...rows.map((r) => r.map(esc).join(';'))];
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

// ── Metadatos de cada incoherencia (título + descripción) ───────────
// Listados ordenados: primero farmacias, luego farmacéuticos. `key` estable e independiente del orden.
interface ListadoDef {
  key: string;
  titulo: string;
  descripcion: string;
}
const LISTADOS: ListadoDef[] = [
  {
    key: 'far_sin_titular_gs',
    titulo: 'Farmacias sin titular en Gescom',
    descripcion:
      'Farmacias activas en Gescom que no tienen titular activo. Al aplicar la regla «un farmacéutico no puede ser titular de dos farmacias» en la reconciliación la farmacia puede quedar sin titular.',
  },
  {
    key: 'far_sin_titular_cu2',
    titulo: 'Farmacias sin titular en CU2',
    descripcion: 'Farmacias en CU2 que no tienen ningún titular (propietario) registrado.',
  },
  {
    key: 'far_no_biunivoca',
    titulo: 'Farmacias — vinculación no biunívoca',
    descripcion:
      'Farmacias CU2 (X → id_farmacia_gs = Y) cuya farmacia de Gescom no apunta de vuelta a X (ID_FARMACIA_CU2): rompe la reciprocidad de la vinculación. Y puede ser el ID de Gescom o el de Auraquantic, el de los vínculos anteriores a la migración.',
  },
  {
    key: 'far_baja_fc',
    titulo: 'Farmacias vinculadas — de baja',
    descripcion: 'Farmacias de Gescom dadas de baja que siguen vinculadas a CU2 (ID_FARMACIA_CU2 no nulo).',
  },
  {
    key: 'fco_titular_empleado',
    titulo: 'Farmacéuticos Gescom — titular y empleado a la vez',
    descripcion:
      'Clientes Gescom que figuran como titular activo de una farmacia y empleado activo de otra (o la misma) simultáneamente.',
  },
  {
    key: 'fco_no_biunivoca',
    titulo: 'Farmacéuticos — vinculación no biunívoca',
    descripcion:
      'Colegiados CU2 (X → id_colegiado_gs = Y) cuyo cliente de Gescom no apunta de vuelta a X (ID_COLEGIADO_CU2): rompe la reciprocidad de la vinculación. Y puede ser el ID de Gescom o el de Auraquantic, el de los vínculos anteriores a la migración.',
  },
  {
    key: 'col_repetida',
    titulo: 'Colegiados CU2 con vinculación repetida (doble colegiación o error)',
    descripcion:
      'Grupos de colegiados CU2 que apuntan al mismo cliente de Gescom (doble colegiación u otros motivos). Los vínculos con un ID ambiguo no se agrupan.',
  },
  {
    key: 'fco_baja_fc',
    titulo: 'Farmacéuticos vinculados — de baja',
    descripcion: 'Clientes de Gescom dados de baja que siguen vinculados a CU2 (ID_COLEGIADO_CU2 no nulo).',
  },
];

// Subtipo de rotura de biunivocidad → etiqueta neutra (tipo de caso, no estado) + nivel de atención para el filete de fila
const SUBTIPO_META: Record<BiunivocidadSubtipo, { label: string; nivel: FctNivelAtencion; ayuda?: string }> = {
  SIN_BACKLINK: { label: 'Sin back-link', nivel: 'atencion' },
  OTRO_CU2: { label: 'Apunta a otro CU2', nivel: 'urgente' },
  ID_INVALIDO: { label: 'id_gs inválido', nivel: 'urgente' },
  // El ID de CU2 es el de Gescom de un registro y el de Auraquantic de otro (#199): transitorio hasta M-20.
  AMBIGUO: {
    label: 'ID ambiguo',
    nivel: 'atencion',
    ayuda: 'El ID es el de Gescom de un registro y el de Auraquantic de otro. Es transitorio: desaparece cuando CU2 pase a IDs de Gescom (M-20).',
  },
};
const subtipoTag = (s: BiunivocidadSubtipo) => <Tag title={SUBTIPO_META[s].ayuda}>{SUBTIPO_META[s].label}</Tag>;
const filaSubtipo = (s: BiunivocidadSubtipo) => claseFilaAtencion(SUBTIPO_META[s].nivel);

const rolTag = (propietario: boolean) =>
  propietario ? <Tag>Titular</Tag> : <Tag>Empleado</Tag>;

const vincFarmaciaTag = (vinc: boolean) =>
  vinc
    ? <FctEstado personalizado={{ etiqueta: 'Vinculada', familia: 'exito' }} />
    : <FctEstado personalizado={{ etiqueta: 'Sin vincular', familia: 'aviso' }} />;

/** Aviso puntual «sin titular en ambos sistemas»: glifo de atención, sin color de pastilla. */
const GlifoSinTitularAmbos: React.FC<{ title?: string }> = ({ title }) => (
  <span title={title} style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}>
    <FctGlifoAtencion nivel="urgente" />
  </span>
);

// ── Tablas por INC ──────────────────────────────────────────────────

const Inc01Table: React.FC<{ rows: Inc01Row[] }> = ({ rows }) => {
  const columns: ColumnsType<Inc01Row> = [
    { title: 'ID', dataIndex: 'id', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.id, b.id) },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      sorter: (a, b) => txt(a.nombre, b.nombre),
      render: (_, r) => (r.nombre),
    },
    {
      title: 'Motivo',
      key: 'motivo',
      render: (_, r) => {
        const m: string[] = [];
        if (r.fBaja) m.push(`Baja=${r.fBaja}`);
        return m.join(', ');
      },
    },
    {
      title: 'Rol',
      key: 'rol',
      render: (_, r) => (
        <>
          {r.esTitular && <Tag>Titular</Tag>}
          {r.esEmpleado && <Tag>Empleado</Tag>}
          {!r.esTitular && !r.esEmpleado && '-'}
        </>
      ),
    },
    { title: 'Vinculado', key: 'vinc', render: (_, r) => (r.idColegiadoCU2 ? <FctEstado personalizado={{ etiqueta: 'Vinculado', familia: 'exito' }} /> : null) },
    { title: 'Nombre CU2', dataIndex: 'nombreColegiadoCU2' },
    { title: 'id_colegiado_CU2', dataIndex: 'idColegiadoCU2', className: 'fct-num', align: 'right' },
  ];
  return <Table rowKey="id" size="small" columns={columns} dataSource={rows} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />;
};

const Inc02Table: React.FC<{ rows: Inc02Row[] }> = ({ rows }) => {
  const columns: ColumnsType<Inc02Row> = [
    { title: 'ID', dataIndex: 'id', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.id, b.id) },
    { title: 'Nombre', dataIndex: 'nombre', sorter: (a, b) => txt(a.nombre, b.nombre), render: (_, r) => (r.nombre) },
    { title: 'Farmacia (titular)', dataIndex: 'farmaciaTitular', className: 'fct-num', align: 'right' },
    { title: 'Farmacia (empleado)', dataIndex: 'farmaciaEmpleado', className: 'fct-num', align: 'right' },
    { title: 'id_colegiado_CU2', dataIndex: 'idColegiadoCU2', className: 'fct-num', align: 'right' },
  ];
  return <Table rowKey="id" size="small" columns={columns} dataSource={rows} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />;
};

// Confianza del análisis de nombres: escala con semáforo natural
const CONF_FAMILIA: Record<string, 'exito' | 'aviso' | 'riesgo'> = { SEGURO: 'exito', DUDOSO: 'aviso', ERROR: 'riesgo' };

const Inc04Table: React.FC<{ rows: Inc04Row[] }> = ({ rows }) => {
  const [selected, setSelected] = useState<React.Key[]>([]);
  // Marca de inicio de grupo (cambia id_colegiado_gs respecto a la fila anterior)
  const groupStart = useMemo(() => {
    const s = new Set<number>();
    let prev: number | null = null;
    rows.forEach((r) => {
      if (prev !== null && r.idColegiadoGs !== prev) s.add(r.idColegiado);
      prev = r.idColegiadoGs;
    });
    return s;
  }, [rows]);

  const columns: ColumnsType<Inc04Row> = [
    { title: 'id_colegiado_gs', dataIndex: 'idColegiadoGs', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idColegiadoGs, b.idColegiadoGs) },
    { title: 'Nombre Gescom', key: 'gescom', render: (_, r) => (r.nombreGescom) },
    { title: 'id_colegiado CU2', dataIndex: 'idColegiado', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idColegiado, b.idColegiado) },
    { title: 'Nombre CU2', dataIndex: 'nombreColegiado', sorter: (a, b) => txt(a.nombreColegiado, b.nombreColegiado) },
    { title: 'Rol', key: 'rol', render: (_, r) => rolTag(r.propietario) },
    { title: 'Num. col.', dataIndex: 'numColegiado', className: 'fct-num', align: 'right' },
    { title: 'Colegio', dataIndex: 'colegio' },
  ];

  const copySelected = async () => {
    if (selected.length === 0) {
      message.info('Ningún colegiado seleccionado');
      return;
    }
    const texto = selected.join(',');
    try {
      await navigator.clipboard.writeText(texto);
      message.success(`${selected.length} id_colegiado copiados`);
    } catch {
      message.warning(texto);
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button size="small" icon={<CopyOutlined />} onClick={copySelected}>
          Copiar id_colegiado seleccionados
        </Button>
        <Text type="secondary">{selected.length > 0 ? <><span className="fct-mono">{selected.length}</span> seleccionados</> : ''}</Text>
      </Space>
      <Table
        rowKey="idColegiado"
        size="small"
        columns={columns}
        dataSource={rows}
        scroll={{ x: true }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
        rowClassName={(r) => (groupStart.has(r.idColegiado) ? 'inc-group-start' : '')}
      />
    </>
  );
};

const Inc05Table: React.FC<{ rows: Inc05Row[] }> = ({ rows }) => {
  const [filtro, setFiltro] = useState<'' | 'vinc' | 'sinv' | 'ambos'>('');
  const counts = useMemo(
    () => ({
      vinc: rows.filter((r) => r.idFarmaciaCU2).length,
      sinv: rows.filter((r) => !r.idFarmaciaCU2).length,
      ambos: rows.filter((r) => r.idFarmaciaCU2 && r.cu2SinTitular).length,
    }),
    [rows],
  );
  const data =
    filtro === 'vinc'
      ? rows.filter((r) => r.idFarmaciaCU2)
      : filtro === 'sinv'
      ? rows.filter((r) => !r.idFarmaciaCU2)
      : filtro === 'ambos'
      ? rows.filter((r) => r.idFarmaciaCU2 && r.cu2SinTitular)
      : rows;

  const farmaciaTxt = (r: Inc05Row) => {
    const d = (r.direccion ?? '').trim();
    const p = (r.poblacion ?? '').trim();
    return d && p ? `${d} - ${p}` : d || p || (r.nombreGescom ?? '').trim() || '(ver ficha)';
  };
  const cu2Txt = (r: Inc05Row) => {
    const d = (r.cu2Direccion ?? '').trim();
    const l = (r.cu2Localidad ?? '').trim();
    return d && l ? `${d} - ${l}` : d || l;
  };

  const columns: ColumnsType<Inc05Row> = [
    { title: 'ID', dataIndex: 'idEntidad', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idEntidad, b.idEntidad) },
    { title: 'Farmacia (Gescom)', key: 'farmacia', render: (_, r) => (farmaciaTxt(r)) },
    { title: 'Provincia', dataIndex: 'provincia', sorter: (a, b) => txt(a.provincia, b.provincia) },
    { title: 'Vinculación', key: 'vinc', render: (_, r) => vincFarmaciaTag(!!r.idFarmaciaCU2) },
    { title: 'id_farmacia_CU2', dataIndex: 'idFarmaciaCU2', className: 'fct-num', align: 'right' },
    {
      title: 'Farmacia CU2',
      key: 'cu2',
      render: (_, r) => (
        <>
          {r.idFarmaciaCU2 && r.cu2SinTitular && (
            <GlifoSinTitularAmbos title="La farmacia CU2 tampoco tiene titular: sin titular en ambos sistemas" />
          )}
          {cu2Txt(r)}
        </>
      ),
    },
    { title: 'Nombre farmacia (CU2)', dataIndex: 'cu2NombreFarmacia' },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Filtrar:</Text>
        <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todas (<span className="fct-mono">{rows.length}</span>)</FilterBtn>
        <FilterBtn active={filtro === 'vinc'} onClick={() => setFiltro('vinc')}>Vinculadas <span className="fct-mono">{counts.vinc}</span></FilterBtn>
        <FilterBtn active={filtro === 'sinv'} onClick={() => setFiltro('sinv')}>Sin vincular <span className="fct-mono">{counts.sinv}</span></FilterBtn>
        <FilterBtn active={filtro === 'ambos'} onClick={() => setFiltro('ambos')}>
          <GlifoSinTitularAmbos />
          Sin titular en ambos <span className="fct-mono">{counts.ambos}</span>
        </FilterBtn>
      </Space>
      {counts.ambos > 0 && (
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          <GlifoSinTitularAmbos /> la contraparte CU2 tampoco tiene titular — sin titular en ambos
          sistemas (<b className="fct-mono">{counts.ambos}</b> casos).
        </Paragraph>
      )}
      <Table
        rowKey="idEntidad"
        size="small"
        columns={columns}
        dataSource={data}
        scroll={{ x: true }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        rowClassName={(r) => claseFilaAtencion(r.idFarmaciaCU2 && r.cu2SinTitular ? 'urgente' : 'normal')}
      />
    </>
  );
};

const Inc06Table: React.FC<{ rows: Inc06Row[] }> = ({ rows }) => {
  const [filtro, setFiltro] = useState<'' | 'vinc' | 'sinv'>('');
  const counts = useMemo(
    () => ({ vinc: rows.filter((r) => r.idFarmaciaGs).length, sinv: rows.filter((r) => !r.idFarmaciaGs).length }),
    [rows],
  );
  const data = filtro === 'vinc' ? rows.filter((r) => r.idFarmaciaGs) : filtro === 'sinv' ? rows.filter((r) => !r.idFarmaciaGs) : rows;

  const columns: ColumnsType<Inc06Row> = [
    { title: 'id_farmacia', dataIndex: 'idFarmacia', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idFarmacia, b.idFarmacia) },
    { title: 'Nombre farmacia', dataIndex: 'nombreFarmacia', sorter: (a, b) => txt(a.nombreFarmacia, b.nombreFarmacia) },
    { title: 'Dirección', dataIndex: 'direccion' },
    { title: 'Localidad', dataIndex: 'localidad', sorter: (a, b) => txt(a.localidad, b.localidad) },
    { title: 'Provincia', dataIndex: 'provincia', sorter: (a, b) => txt(a.provincia, b.provincia) },
    { title: 'Vinculación', key: 'vinc', render: (_, r) => vincFarmaciaTag(!!r.idFarmaciaGs) },
    { title: 'id_farmacia_gs', dataIndex: 'idFarmaciaGs', className: 'fct-num', align: 'right' },
    { title: 'Farmacia Gescom', key: 'gescom', render: (_, r) => (r.idFarmaciaGs ? (r.nombreGescom || '—') : null) },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Filtrar:</Text>
        <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todas (<span className="fct-mono">{rows.length}</span>)</FilterBtn>
        <FilterBtn active={filtro === 'vinc'} onClick={() => setFiltro('vinc')}>Vinculadas <span className="fct-mono">{counts.vinc}</span></FilterBtn>
        <FilterBtn active={filtro === 'sinv'} onClick={() => setFiltro('sinv')}>Sin vincular <span className="fct-mono">{counts.sinv}</span></FilterBtn>
      </Space>
      <Table rowKey="idFarmacia" size="small" columns={columns} dataSource={data} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />
    </>
  );
};

const SUBTIPOS: BiunivocidadSubtipo[] = ['SIN_BACKLINK', 'OTRO_CU2', 'ID_INVALIDO', 'AMBIGUO'];

const Inc07Table: React.FC<{ rows: Inc07Row[] }> = ({ rows }) => {
  const [filtro, setFiltro] = useState<'' | BiunivocidadSubtipo>('');
  const [ocultarDobles, setOcultarDobles] = useState(false);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    SUBTIPOS.forEach((s) => (c[s] = rows.filter((r) => r.subtipo === s).length));
    return c;
  }, [rows]);
  const nDobles = useMemo(() => rows.filter((r) => r.dobleColegiacion).length, [rows]);
  let data = filtro ? rows.filter((r) => r.subtipo === filtro) : rows;
  if (ocultarDobles) data = data.filter((r) => !r.dobleColegiacion);

  const columns: ColumnsType<Inc07Row> = [
    { title: 'id_colegiado CU2', dataIndex: 'idColegiado', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idColegiado, b.idColegiado) },
    { title: 'Nombre CU2', dataIndex: 'nombreColegiado', sorter: (a, b) => txt(a.nombreColegiado, b.nombreColegiado) },
    { title: 'id_colegiado_gs', dataIndex: 'idColegiadoGs', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idColegiadoGs, b.idColegiadoGs) },
    {
      title: 'Subtipo',
      key: 'subtipo',
      render: (_, r) => (
        <Space size={4}>
          {subtipoTag(r.subtipo)}
          {r.dobleColegiacion && <Tag>Doble colegiación</Tag>}
        </Space>
      ),
    },
    {
      title: 'Back-link actual',
      key: 'backlink',
      className: 'fct-num',
      align: 'right',
      render: (_, r) => (r.backlinkActual != null ? r.backlinkActual : '—'),
    },
    { title: 'Gescom', key: 'gescom', render: (_, r) => (r.subtipo === 'ID_INVALIDO' ? '—' : (r.nombreGescom || '—')) },
    {
      title: 'Confianza',
      key: 'confianza',
      sorter: (a, b) => num(a.score, b.score),
      render: (_, r) =>
        r.confianza ? (
          <Space size={4}>
            <FctEstado personalizado={{ etiqueta: r.confianza, familia: CONF_FAMILIA[r.confianza] ?? 'neutro' }} />
            <Text type="secondary" className="fct-mono" style={{ fontSize: 12 }}>{pctStr(r.score ?? 0)}</Text>
          </Space>
        ) : (
          '—'
        ),
    },
    { title: 'Rol', key: 'rol', render: (_, r) => rolTag(r.propietario) },
    { title: 'Num. col.', dataIndex: 'numColegiado', className: 'fct-num', align: 'right' },
    { title: 'Colegio', dataIndex: 'colegio' },
  ];

  const doExport = () =>
    exportCsv(
      'INC-07_vinculacion_no_biunivoca_farmaceuticos.csv',
      ['id_colegiado_CU2', 'Nombre_CU2', 'id_colegiado_gs', 'Subtipo', 'Doble_colegiacion', 'Backlink_actual', 'Nombre_Gescom', 'Confianza', 'Score', 'Rol', 'Num_colegiado', 'Colegio'],
      data.map((r) => [
        r.idColegiado,
        r.nombreColegiado ?? '',
        r.idColegiadoGs,
        SUBTIPO_META[r.subtipo].label,
        r.dobleColegiacion ? 'Sí' : '',
        r.backlinkActual ?? '',
        r.nombreGescom ?? '',
        r.confianza ?? '',
        r.score != null ? pctStr(r.score) : '',
        r.propietario ? 'Titular' : 'Empleado',
        r.numColegiado ?? '',
        r.colegio ?? '',
      ]),
    );

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Filtrar:</Text>
        <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todos (<span className="fct-mono">{rows.length}</span>)</FilterBtn>
        {SUBTIPOS.map((s) => (
          <FilterBtn key={s} active={filtro === s} onClick={() => setFiltro(s)}>
            {SUBTIPO_META[s].label} <span className="fct-mono">{counts[s]}</span>
          </FilterBtn>
        ))}
        {nDobles > 0 && (
          <FilterBtn active={ocultarDobles} onClick={() => setOcultarDobles((v) => !v)}>
            {ocultarDobles ? 'Mostrar' : 'Ocultar'} dobles colegiaciones (<span className="fct-mono">{nDobles}</span>)
          </FilterBtn>
        )}
        <Button size="small" icon={<DownloadOutlined />} onClick={doExport}>Exportar CSV</Button>
      </Space>
      <Table rowKey="idColegiado" size="small" columns={columns} dataSource={data} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} rowClassName={(r) => filaSubtipo(r.subtipo)} />
    </>
  );
};

const Inc08Table: React.FC<{ rows: Inc08Row[] }> = ({ rows }) => {
  const [filtro, setFiltro] = useState<'' | BiunivocidadSubtipo>('');
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    SUBTIPOS.forEach((s) => (c[s] = rows.filter((r) => r.subtipo === s).length));
    return c;
  }, [rows]);
  const data = filtro ? rows.filter((r) => r.subtipo === filtro) : rows;

  const columns: ColumnsType<Inc08Row> = [
    { title: 'id_farmacia', dataIndex: 'idFarmacia', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idFarmacia, b.idFarmacia) },
    { title: 'Nombre farmacia (CU2)', dataIndex: 'nombreFarmacia', sorter: (a, b) => txt(a.nombreFarmacia, b.nombreFarmacia) },
    { title: 'id_farmacia_gs', dataIndex: 'idFarmaciaGs', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idFarmaciaGs, b.idFarmaciaGs) },
    { title: 'Subtipo', key: 'subtipo', render: (_, r) => subtipoTag(r.subtipo) },
    { title: 'Back-link actual', key: 'backlink', className: 'fct-num', align: 'right', render: (_, r) => (r.backlinkActual != null ? r.backlinkActual : '—') },
    { title: 'Farmacia Gescom', key: 'gescom', render: (_, r) => (r.subtipo === 'ID_INVALIDO' ? '—' : (r.nombreGescom || '—')) },
  ];

  return (
    <>
      <Space wrap style={{ marginBottom: 12 }}>
        <Text type="secondary">Filtrar:</Text>
        <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>Todas (<span className="fct-mono">{rows.length}</span>)</FilterBtn>
        {SUBTIPOS.map((s) => (
          <FilterBtn key={s} active={filtro === s} onClick={() => setFiltro(s)}>
            {SUBTIPO_META[s].label} <span className="fct-mono">{counts[s]}</span>
          </FilterBtn>
        ))}
      </Space>
      <Table rowKey="idFarmacia" size="small" columns={columns} dataSource={data} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} rowClassName={(r) => filaSubtipo(r.subtipo)} />
    </>
  );
};

const Inc09Table: React.FC<{ rows: Inc09Row[] }> = ({ rows }) => {
  const columns: ColumnsType<Inc09Row> = [
    { title: 'ID', dataIndex: 'idEntidad', className: 'fct-num', align: 'right', sorter: (a, b) => num(a.idEntidad, b.idEntidad) },
    {
      title: 'Farmacia (Gescom)',
      dataIndex: 'nombre',
      sorter: (a, b) => txt(a.nombre, b.nombre),
      render: (_, r) => (r.nombre),
    },
    {
      title: 'Motivo',
      key: 'motivo',
      render: (_, r) => {
        const m: string[] = [];
        if (r.fBaja) m.push(`Baja=${r.fBaja}`);
        return m.join(', ');
      },
    },
    { title: 'Vinculada CU2', key: 'vinc', render: (_, r) => (r.idFarmaciaCU2 ? <FctEstado personalizado={{ etiqueta: 'Vinculada', familia: 'exito' }} /> : null) },
    { title: 'id_farmacia_CU2', dataIndex: 'idFarmaciaCU2', className: 'fct-num', align: 'right' },
    { title: 'Titular activo', key: 'tit', render: (_, r) => (r.tieneTitularActivo ? <Tag>Con titular</Tag> : null) },
  ];
  return <Table rowKey="idEntidad" size="small" columns={columns} dataSource={rows} scroll={{ x: true }} pagination={{ pageSize: 50, showSizeChanger: true }} />;
};

// ── Componente principal ────────────────────────────────────────────
const VinculacionIncoherenciasTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<PanelIncoherencias | null>(null);
  const [sel, setSel] = useState('far_sin_titular_gs');

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await getPanelVinculacionIncoherencias());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <Paragraph type="secondary" style={{ marginTop: 12 }}>
          Calculando incoherencias (incluye análisis de nombres)…
        </Paragraph>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert type="error" showIcon message="No se pudieron cargar las incoherencias" action={<Button onClick={load}>Reintentar</Button>} />
    );
  }

  const counts: Record<string, number> = {
    far_sin_titular_gs: data.inc05.length,
    far_sin_titular_cu2: data.inc06.length,
    far_no_biunivoca: data.inc08.length,
    far_baja_fc: data.inc09.length,
    fco_titular_empleado: data.inc02.length,
    fco_no_biunivoca: data.inc07.length,
    col_repetida: data.inc04.length,
    fco_baja_fc: data.inc01.length,
  };

  const renderTabla = () => {
    switch (sel) {
      case 'far_sin_titular_gs': return <Inc05Table rows={data.inc05} />;
      case 'far_sin_titular_cu2': return <Inc06Table rows={data.inc06} />;
      case 'far_no_biunivoca': return <Inc08Table rows={data.inc08} />;
      case 'far_baja_fc': return <Inc09Table rows={data.inc09} />;
      case 'fco_titular_empleado': return <Inc02Table rows={data.inc02} />;
      case 'fco_no_biunivoca': return <Inc07Table rows={data.inc07} />;
      case 'col_repetida': return <Inc04Table rows={data.inc04} />;
      case 'fco_baja_fc': return <Inc01Table rows={data.inc01} />;
      default: return null;
    }
  };

  const meta = LISTADOS.find((l) => l.key === sel) ?? LISTADOS[0];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load}>Actualizar</Button>
      </div>
      <Row gutter={16}>
        <Col xs={24} md={7} lg={6}>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {LISTADOS.map((l) => {
              const activo = l.key === sel;
              const c = counts[l.key];
              return (
                <Card
                  key={l.key}
                  size="small"
                  hoverable
                  onClick={() => setSel(l.key)}
                  styles={{ body: { padding: 12 } }}
                  style={activo ? { borderColor: fct.interaccion['seleccion-borde'], borderWidth: 2 } : undefined}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: fct.tinta['700'] }}>{l.titulo}</div>
                    <Badge count={c} showZero color={c === 0 ? fct.estado.exito.linea : fct.estado.aviso.linea} overflowCount={99999} />
                  </div>
                </Card>
              );
            })}
          </Space>
        </Col>
        <Col xs={24} md={17} lg={18}>
          <Card size="small">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 24,
                flexWrap: 'wrap',
                marginBottom: 12,
              }}
            >
              <Title level={5} style={{ margin: 0 }}>
                {meta.titulo}
              </Title>
              <Text type="secondary" style={{ fontSize: 13, textAlign: 'right', flex: 1, minWidth: 240 }}>
                {meta.descripcion}
              </Text>
            </div>
            {renderTabla()}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default VinculacionIncoherenciasTab;
